// Manual vs Scout on the Activity page. Rendering rules only: which work is whose is decided in
// activity.py (`lane_of`, auto_send/lane_accounts.py) and arrives on the payload.
//
// - The strategist rows are their manual work. The Scout row is the auto lane's sequences,
//   whoever's mailbox sent them, picked from the same Strategist select as a person.
// - After a real reply a Scout account is the strategist's, and its replies and meetings count on
//   both rows. The team totals count each once, so the rows can add up to more.
//
// Pure, and imported by `node --test`.

import type { ActivityPayload, ActivityWeek, StageMember, StrategistRow } from '../api/types';

// The Scout row's key. Matches `activity.SCOUT_ROW_KEY`; never an address.
export const SCOUT_KEY = 'scout';

export function isScoutRow(row: StrategistRow | null | undefined): boolean {
  return row?.sdr_owner_email === SCOUT_KEY;
}

// The by-strategist table and the picker: every strategist, then Scout.
export function tableRows(payload: ActivityPayload): StrategistRow[] {
  return payload.scout ? [...payload.strategists, payload.scout] : payload.strategists;
}

// The selected row, Scout included, or null for the whole team.
export function findScopedRow(payload: ActivityPayload, selected: string | null): StrategistRow | null {
  if (!selected) return null;
  return tableRows(payload).find(candidate => candidate.sdr_owner_email === selected) ?? null;
}

// A drawer's entries for the selected row. A strategist sees their own manual and shared
// entries; Scout sees every entry its sequences sourced, whoever owns the account.
export function scopeMembers(members: StageMember[], row: StrategistRow | null): StageMember[] {
  if (!row) return members;
  if (isScoutRow(row)) return members.filter(member => member.scout === true);
  return members.filter(member => member.sdr_owner === row.sdr_owner && member.manual !== false);
}

// The weekly trend. The team view uses the de-duplicated stream, so a reply shared by a
// strategist and Scout is one reply; a scoped view uses that row's own weeks.
export function scopeWeeks(payload: ActivityPayload, selected: string | null): ActivityWeek[] {
  if (!selected) return payload.weekly_team ?? payload.weekly;
  return payload.weekly.filter(week => week.sdr_owner_email === selected);
}

// The "Manual 12 · Scout 4" line under a team tile, from already formatted figures.
export function laneSplitText(manual: string, scout: string): string {
  return `Manual ${manual} · Scout ${scout}`;
}
